/**
 * Metrics singleton for Spaces 3.
 *
 * Creates the shared event emitter, attaches it to window.SPACES.events so
 * external analytics consumers can subscribe, and exposes typed helper
 * functions the React app calls to emit each metrics event.
 */

import type { ApiUnit, ApiFloorPlan } from '@/types/api'
import type { SpacesEventEmitter, ViewChangeMode } from '@/types/metrics'
import { createEmitter } from '@/metrics/emitter'
import { toMetricsUnit, toMetricsPlan } from '@/metrics/payloads'

let emitter: SpacesEventEmitter | null = null

/**
 * Initialise the emitter and publish it on window.SPACES.events.
 * Idempotent — repeated calls return the existing instance.
 */
export function initMetrics (): SpacesEventEmitter {
	if (emitter) return emitter

	emitter = createEmitter()

	if (typeof window !== 'undefined') {
		window.SPACES = window.SPACES ?? ({} as NonNullable<Window['SPACES']>)
		window.SPACES.events = emitter
	}

	return emitter
}

function getEmitter (): SpacesEventEmitter {
	return emitter ?? initMetrics()
}

/** Signal that the app is mounted and will emit metrics events. */
export function emitReady (): void {
	getEmitter().emit('ready', {
		name: 'ready',
		data: { message: 'SPACES is ready' }
	})
}

/** Emit the current page of units shown on the index. */
export function emitUnitsChange (units: ApiUnit[]): void {
	getEmitter().emit('metrics.units.change', {
		name: 'metrics.units.change',
		data: { units: units.map(toMetricsUnit) }
	})
}

/** Emit the current page of plans shown on the index. */
export function emitPlansChange (plans: ApiFloorPlan[]): void {
	getEmitter().emit('metrics.plans.change', {
		name: 'metrics.plans.change',
		data: { plans: plans.map(toMetricsPlan) }
	})
}

/** Fired when the unit detail view loads. */
export function emitUnitDetailsClick (unit: ApiUnit): void {
	getEmitter().emit('metrics.unit.details.click', {
		name: 'metrics.unit.details.click',
		data: { unit: toMetricsUnit(unit) }
	})
}

/** Fired when the plan detail view loads. */
export function emitPlanDetailsClick (plan: ApiFloorPlan): void {
	getEmitter().emit('metrics.plan.details.click', {
		name: 'metrics.plan.details.click',
		data: { plan: toMetricsPlan(plan) }
	})
}

/** Fired when the main apply CTA on the unit detail view is clicked. */
export function emitUnitApplyClick (unit: ApiUnit): void {
	getEmitter().emit('metrics.unit.apply.click', {
		name: 'metrics.unit.apply.click',
		data: { unit: toMetricsUnit(unit) }
	})
}

/**
 * Fired synchronously before the index switches view mode. Returns any map URL
 * override a consumer supplied by mutating `payload.data.mapUrl` (WordPress
 * `apply_filters` style). The override is only meaningful when switching to the
 * full map-only view (`to === 'map'`); `null` means "use the default embed".
 */
export function emitViewChangeBefore (from: ViewChangeMode, to: ViewChangeMode): string | null {
	const payload = {
		name: 'view.change.before',
		data: { from, to, mapUrl: null as string | null }
	}
	getEmitter().emit('view.change.before', payload)
	return payload.data.mapUrl
}
